import React, { useEffect, useState } from "react";
import { Form, ModalDialog, StringField, SubmitButton } from "@culturehq/components";

import { useDoor, useSignUpload, useTryCatch } from "app/hooks";
import { Trans } from "app/utils";
import ErrorMessage from "components/common/ErrorMessage";
import ChqModalDialog from "components/common/ChqModalDialog";

import SelectImage from "components/common/SelectImage";
import ProgressLine from "components/admin/analytics/ProgressLine";
import { VirtualBackground } from "./VirtualBackgroundTypes";

import "./virtual-backgrounds.scss";

type PartialDefaultPrompt = Partial<VirtualBackground>;
interface DefaultPromptModalProps {
  children: (callback: () => void) => React.ReactNode;
  virtualBackground?: VirtualBackground;
  onCreate?: (virtualBackground: VirtualBackground) => void;
  onUpdate?: (virtualBackground: VirtualBackground) => void;
}

const mediaType = (file: File | string) => {
  if (typeof file === "string" || file.type.includes("image")) return "image";
  return "video";
};

const VirtualBackgroundModal = ({
  children,
  virtualBackground,
  onCreate = () => {},
  onUpdate = () => {}
}: DefaultPromptModalProps) => {
  const { open, onOpen, onClose } = useDoor(false);
  const [media, setMedia] = useState<string | File>();
  const [thumbnail, setThumbnail] = useState<File>();
  const [progress, setProgress] = useState<number>();

  const signUpload = useSignUpload();
  const mediaUrl = virtualBackground?.thumbnail || virtualBackground?.url;

  useEffect(() => {
    if (open) {
      if (mediaUrl) setMedia(mediaUrl);
    } else {
      setMedia(undefined);
      setThumbnail(undefined);
      setProgress(undefined);
    }
  }, [mediaUrl, open]);

  const uploadImage = (file: File, progress = true) => {
    if (typeof file === "string") return file;

    const mediaUpload = 2;
    return signUpload(file, progress ? (mediaProgress: number) => {
      const localProgress = (100 / mediaUpload) + (mediaProgress / mediaUpload);
      setProgress(localProgress);
    } : undefined);
  };

  const [{ error }, onSubmit] = useTryCatch(
    async (values: PartialDefaultPrompt) => {
      if (!media) return;

      const mediaUrl = await uploadImage(media as File);
      const thumbnailUrl = thumbnail ? await uploadImage(thumbnail, false) : null;
      const payload = {
        ...(virtualBackground || {}),
        ...values,
        url: mediaUrl,
        thumbnail: thumbnailUrl,
        mediaType: mediaType(media)
      };

      if (virtualBackground) onUpdate(payload as VirtualBackground);
      else onCreate(payload as VirtualBackground);

      onClose();
    },
    [virtualBackground, media, thumbnail]
  );

  if (error && error.status !== 422) {
    throw error;
  }

  const ModalTitle = () => (
    <Trans>
      {virtualBackground ? "Edit Virtual Background" : "Add Virtual Background"}
    </Trans>
  );

  const handleBannerImageSelected = async (originalFile: string | File, editedFile: File) => {
    if ((originalFile as File)?.type?.includes("video")) {
      setMedia(originalFile);
      setThumbnail(editedFile);
    } else {
      setMedia(editedFile);
      setThumbnail(undefined);
    }
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog onClose={onClose}>
          <ModalDialog.Heading onClose={onClose}>
            <ModalTitle />
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            <Form
              onSubmit={onSubmit}
              initialValues={{
                url: mediaUrl,
                position: virtualBackground?.position
              }}
            >
              <SelectImage
                previewTitle="Upload file"
                buttonTitle="Upload media"
                fieldTitle="Media"
                mediaType="all"
                imageToShow={mediaUrl}
                aspectRatio={16 / 9}
                onImageSelected={handleBannerImageSelected}
              />
              <StringField name="position" required>
                <Trans>Order</Trans>
              </StringField>
              {progress && (
                <div className="progressline-trend progress-bar">
                  <ProgressLine
                    className="story-progressbar"
                    color="#79b17d"
                    valueNow={progress}
                    valueMax={100}
                  />
                </div>
              )}
              <SubmitButton primary>
                {submitting => (
                  submitting ? <Trans>Submitting...</Trans> : <Trans>Save</Trans>
                )}
              </SubmitButton>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default VirtualBackgroundModal;
